import { dayjs } from "@equidefi/i18n";
import jwtDecode from "jwt-decode";
import { useCookies } from "react-cookie";
import { BASE_COOKIE_OPTIONS } from "@equidefi/constants/auth";
import { useCallback } from "react";

const ACCESS_TOKEN_KEY = "user-token";
const REFRESH_TOKEN_KEY = "user-refresh";

const useUserToken = () => {
  const [cookies, setCookie, removeCookie] = useCookies([ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY]);

  const getExpiration = useCallback(
    (token: string) => {
      let decoded: any;

      try {
        // Decode the token. Will fail if token is invalid
        decoded = jwtDecode(token);
      } catch (e) {
        console.error(e);
        return null;
      }

      return dayjs(decoded.exp * 1000);
    },
    [dayjs, jwtDecode],
  );

  const UserToken = {
    getAccessToken() {
      return cookies[ACCESS_TOKEN_KEY];
    },

    getRefreshToken() {
      return cookies[REFRESH_TOKEN_KEY];
    },

    valid(token?: string | null) {
      if (!token) return false;

      const expirationDate = getExpiration(token);
      if (!expirationDate) return false;

      const isValid = expirationDate.isAfter(dayjs());

      return !!isValid;
    },

    setAccessToken(value: string) {
      const expirationDate = getExpiration(value);

      setCookie(ACCESS_TOKEN_KEY, value, {
        ...BASE_COOKIE_OPTIONS,
        expires: expirationDate?.subtract(1, "h")?.toDate(),
      });
    },

    setRefreshToken(value: string) {
      const expirationDate = getExpiration(value);

      setCookie(REFRESH_TOKEN_KEY, value, {
        ...BASE_COOKIE_OPTIONS,
        expires: expirationDate?.toDate(),
      });
    },

    clear() {
      removeCookie(ACCESS_TOKEN_KEY, BASE_COOKIE_OPTIONS);
      removeCookie(REFRESH_TOKEN_KEY, BASE_COOKIE_OPTIONS);
    },

    clearAccessToken() {
      removeCookie(ACCESS_TOKEN_KEY, BASE_COOKIE_OPTIONS);
    },
  };

  return UserToken;
};

export default useUserToken;
